"use strict";
// (C) 2023 GoodData Corporation
Object.defineProperty(exports, "__esModule", { value: true });
exports.useShareDialogInteraction = exports.ComponentInteractionProvider = exports.useComponentInteractionContext = void 0;
var tslib_1 = require("tslib");
var react_1 = tslib_1.__importStar(require("react"));
var noop_1 = tslib_1.__importDefault(require("lodash/noop"));
var uuid_1 = require("uuid");
var utils_1 = require("./utils");
var types_1 = require("./types");
var defaultContext = {
    onInteraction: noop_1.default,
    currentUser: undefined,
};
var ComponentInteractionContext = react_1.default.createContext(defaultContext);
/**
 * @internal
 */
exports.useComponentInteractionContext = function () {
    return react_1.useContext(ComponentInteractionContext);
};
/**
 * @internal
 */
exports.ComponentInteractionProvider = function (props) {
    var children = props.children, onInteraction = props.onInteraction, currentUser = props.currentUser, currentUserPermissions = props.currentUserPermissions, isCurrentUserWorkspaceManager = props.isCurrentUserWorkspaceManager, isSharedObjectLocked = props.isSharedObjectLocked, sharedObjectStatus = props.sharedObjectStatus;
    var flowId = react_1.useMemo(function () { return uuid_1.v4(); }, []);
    var currentUserPermission = react_1.useMemo(function () { return utils_1.getGranularPermissionFromUserPermissions(currentUserPermissions); }, [currentUserPermissions]);
    var handleInteraction = react_1.useCallback(function (data) {
        onInteraction(tslib_1.__assign(tslib_1.__assign({}, data), { flowId: flowId,
            currentUserPermission: currentUserPermission,
            isSharedObjectLocked: isSharedObjectLocked,
            sharedObjectStatus: sharedObjectStatus,
            isCurrentUserWorkspaceManager: isCurrentUserWorkspaceManager }));
    }, [
        onInteraction,
        flowId,
        currentUserPermission,
        isSharedObjectLocked,
        sharedObjectStatus,
        isCurrentUserWorkspaceManager,
    ]);
    return (react_1.default.createElement(ComponentInteractionContext.Provider, { value: {
            onInteraction: handleInteraction,
            currentUser: currentUser,
        } }, children));
};
exports.useShareDialogInteraction = function () {
    var _a = exports.useComponentInteractionContext(), onInteraction = _a.onInteraction, currentUser = _a.currentUser;
    var openInteraction = react_1.useCallback(function () {
        return onInteraction({
            type: "SHARE_DIALOG_OPENED",
        });
    }, [onInteraction]);
    var closeInteraction = react_1.useCallback(function () {
        return onInteraction({
            type: "SHARE_DIALOG_CLOSED",
        });
    }, [onInteraction]);
    var saveInteraction = react_1.useCallback(function () {
        return onInteraction({
            type: "SHARE_DIALOG_SAVED",
        });
    }, [onInteraction]);
    var permissionsDropdownOpenInteraction = react_1.useCallback(function (grantee, isExistingGrantee, granteeEffectivePermission) {
        if (!types_1.isGranularGrantee(grantee)) {
            return;
        }
        onInteraction({
            type: "SHARE_DIALOG_PERMISSIONS_DROPDOWN_OPENED",
            isCurrentUserSelfUpdating: utils_1.getIsGranteeCurrentUser(grantee.id, currentUser),
            isExistingGrantee: isExistingGrantee,
            granteeType: types_1.isGranularGranteeUser(grantee) ? "user" : "group",
            granteeEffectivePermission: granteeEffectivePermission,
        });
    }, [onInteraction, currentUser]);
    var permissionsChangeInteraction = react_1.useCallback(function (grantee, isExistingGrantee, granteeEffectivePermission, granteeUpdatedPermission) {
        if (!types_1.isGranularGrantee(grantee)) {
            return;
        }
        onInteraction({
            type: "SHARE_DIALOG_PERMISSIONS_CHANGED",
            isCurrentUserSelfUpdating: utils_1.getIsGranteeCurrentUser(grantee.id, currentUser),
            isExistingGrantee: isExistingGrantee,
            granteeType: types_1.isGranularGranteeUser(grantee) ? "user" : "group",
            granteeEffectivePermission: granteeEffectivePermission,
            granteeUpdatedPermission: granteeUpdatedPermission,
        });
    }, [onInteraction, currentUser]);
    var permissionsRemoveInteraction = react_1.useCallback(function (grantee, isExistingGrantee, granteeEffectivePermission) {
        if (!types_1.isGranularGrantee(grantee)) {
            return;
        }
        onInteraction({
            type: "SHARE_DIALOG_GRANTEE_REMOVED",
            isCurrentUserSelfUpdating: utils_1.getIsGranteeCurrentUser(grantee.id, currentUser),
            isExistingGrantee: isExistingGrantee,
            granteeType: types_1.isGranularGranteeUser(grantee) ? "user" : "group",
            granteeEffectivePermission: granteeEffectivePermission,
        });
    }, [onInteraction, currentUser]);
    var granteeAddInteraction = react_1.useCallback(function (grantee) {
        if (!types_1.isGranularGrantee(grantee)) {
            return;
        }
        onInteraction({
            type: "SHARE_DIALOG_GRANTEE_ADDED",
            granteeType: types_1.isGranularGranteeUser(grantee) ? "user" : "group",
        });
    }, [onInteraction]);
    var availableGranteeListOpenInteraction = react_1.useCallback(function (numberOfAvailableGrantees) {
        return onInteraction({ type: "SHARE_DIALOG_AVAILABLE_GRANTEE_LIST_OPENED", numberOfAvailableGrantees: numberOfAvailableGrantees });
    }, [onInteraction]);
    return {
        openInteraction: openInteraction,
        closeInteraction: closeInteraction,
        saveInteraction: saveInteraction,
        permissionsDropdownOpenInteraction: permissionsDropdownOpenInteraction,
        permissionsChangeInteraction: permissionsChangeInteraction,
        permissionsRemoveInteraction: permissionsRemoveInteraction,
        granteeAddInteraction: granteeAddInteraction,
        availableGranteeListOpenInteraction: availableGranteeListOpenInteraction,
    };
};
//# sourceMappingURL=ComponentInteractionContext.js.map